import React, { useState, useEffect } from 'react';
import { Modal, Input, Upload, Button, message, Form, Select } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;

const EditProductModal = ({ show, onClose, onUpdate, product, submitting }) => {
  const [form] = Form.useForm();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Predefined categories
  const availableCategories = [
    'Mobility',
    'Sensory',
    'Educational',
    'Communication',
    'Daily Living',
    'Recreation',
    'Safety',
    'Other'
  ];

  useEffect(() => {
    if (product && show) {
      form.setFieldsValue({
        name: product.name,
        description: product.description,
        link: product.link,
        category: product.category || []
      });
      setImagePreview(product.image);
      setImageFile(null);
    }
  }, [product, show, form]);

  const handleImageUpload = (file) => {
    // Check file type
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }

    // Check file size (limit to 5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return false;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    return false; // Prevent automatic upload
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onUpdate({
        ...values,
        imageFile: imageFile
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setImageFile(null);
    setImagePreview(null);
    onClose();
  };

  return (
    <Modal
      title="Edit Product"
      visible={show}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Update"
      cancelText="Cancel"
      confirmLoading={submitting}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          name="name"
          label="Product Name"
          rules={[{ required: true, message: 'Please enter product name' }]}
        >
          <Input placeholder="Enter product name" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: 'Please enter product description' }]}
        >
          <TextArea 
            rows={4} 
            placeholder="Enter product description"
          />
        </Form.Item>

        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: 'Please select at least one category' }]}
        >
          <Select
            mode="multiple"
            placeholder="Select categories"
            style={{ width: '100%' }}
          >
            {availableCategories.map(cat => (
              <Option key={cat} value={cat}>
                {cat}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="link"
          label="Product Link"
          rules={[
            { required: true, message: 'Please enter product link' },
            { type: 'url', message: 'Please enter a valid URL' }
          ]}
        >
          <Input placeholder="https://example.com/product" />
        </Form.Item>

        <Form.Item label="Product Image">
          {imagePreview && (
            <div style={{ marginBottom: 16 }}>
              <img 
                src={imagePreview} 
                alt="Product preview" 
                style={{ 
                  maxWidth: '200px', 
                  maxHeight: '200px',
                  objectFit: 'contain',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '8px'
                }} 
              />
            </div>
          )}
          <Upload
            beforeUpload={handleImageUpload}
            showUploadList={false}
            accept="image/*"
          >
            <Button icon={<UploadOutlined />}>
              {imageFile ? 'Change Image' : 'Upload New Image'}
            </Button>
          </Upload>
          {imageFile && (
            <div style={{ marginTop: 8, color: '#52c41a' }}>
              New image selected: {imageFile.name}
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditProductModal; 